import { useState, useEffect } from 'react';
import { XMarkIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { groupAgentsService } from '../../../services/groupAgentsService';
import { userService } from '../../../services/userService';
import { User } from '../../../types/user';
import { useTheme } from '../../../contexts/ThemeContext';
import { Dialog, DialogContent, DialogTitle } from '../../ui/custom/Dialog';
import { useTranslation } from 'react-i18next';

interface ManageAgentsModalProps {
  group: {
    id: number;
    name: string;
  };
  onClose: () => void;
  refreshData: () => void;
}

export default function ManageAgentsModal({ group, onClose, refreshData }: ManageAgentsModalProps) {
  const [currentAgents, setCurrentAgents] = useState<User[]>([]);
  const [availableAgents, setAvailableAgents] = useState<User[]>([]);
  const [selectedAvailableAgents, setSelectedAvailableAgents] = useState<number[]>([]);
  const [selectedCurrentAgents, setSelectedCurrentAgents] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { theme } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    fetchAgents();
  }, [group.id]);

  const fetchAgents = async () => {
    try {
      const [currentAgents, availableAgents] = await Promise.all([
        groupAgentsService.getAgentsByGroupId(group.id),
        groupAgentsService.getAvailableAgents(group.id)
      ]);

      // Transform group agents into users before setting state
      const currentUsers = await Promise.all(
        currentAgents.map(async (agent) => {
          const userData = await userService.getUser(agent.user_id);
          return userData;
        })
      );

      setCurrentAgents(currentUsers);
      setAvailableAgents(availableAgents);
    } catch (err) {
      setError(t('settings.failedToLoadAgents', 'Failed to load agents'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAgents = async () => {
    if (selectedAvailableAgents.length === 0) return;

    try {
      if (selectedAvailableAgents.length === 1) {
        await groupAgentsService.addAgentToGroup(group.id, selectedAvailableAgents[0]);
      } else {
        await groupAgentsService.addAgentsToGroup(group.id, selectedAvailableAgents);
      }
      setSelectedAvailableAgents([]);
      setSelectedCurrentAgents([]);
      await fetchAgents();
      refreshData();
    } catch (err) {
      setError(t('settings.failedToAddAgents', 'Failed to add agents'));
      console.error(err);
    }
  };

  const handleRemoveAgents = async () => {
    if (selectedCurrentAgents.length === 0) return;

    try {
      await groupAgentsService.removeAgentsFromGroup(group.id, selectedCurrentAgents);
      setSelectedCurrentAgents([]);
      setSelectedAvailableAgents([]);
      await fetchAgents();
      refreshData();
    } catch (err) {
      setError(t('settings.failedToRemoveAgents', 'Failed to remove agents'));
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <Dialog open={true}>
        <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
          <div className="p-4 text-center">
            <div className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>
              {t('common.loading', 'Loading...')}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
        <div className="sm:flex sm:items-start">
          <div
            className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
              theme === 'dark' ? 'bg-indigo-900/30' : 'bg-indigo-100'
            }`}
          >
            <UserGroupIcon
              className={`h-6 w-6 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
            <DialogTitle
              className={`text-base font-semibold leading-6 ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}
            >
              {t('settings.manageAgents', 'Manage Agents')} - {group.name}
            </DialogTitle>

            {error && (
              <div
                className={`mt-2 p-3 rounded-md ${
                  theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'
                }`}
              >
                <p className={`text-sm ${theme === 'dark' ? 'text-red-200' : 'text-red-700'}`}>
                  {error}
                </p>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-4">
              {/* Available Agents */}
              <div>
                <h4
                  className={`text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {t('settings.availableAgents', 'Available Agents')}
                </h4>
                <div
                  className={`rounded-md border overflow-y-auto max-h-96 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  {availableAgents.map((agent) => (
                    <label
                      key={agent.id}
                      className={`flex items-center p-2 ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAvailableAgents.includes(agent.id)}
                        onChange={(e) => {
                          setSelectedAvailableAgents(
                            e.target.checked
                              ? [...selectedAvailableAgents, agent.id]
                              : selectedAvailableAgents.filter((id) => id !== agent.id)
                          );
                        }}
                        className={`h-4 w-4 rounded ${
                          theme === 'dark'
                            ? 'text-indigo-500 border-gray-600 focus:ring-offset-gray-800'
                            : 'text-indigo-600 border-gray-300'
                        } focus:ring-indigo-500`}
                      />
                      <span
                        className={`ml-2 text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                        }`}
                      >
                        {agent.full_name || agent.email}
                      </span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={handleAddAgents}
                  disabled={selectedAvailableAgents.length === 0}
                  className={`mt-2 w-full inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 ${
                    theme === 'dark'
                      ? 'bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-500 focus:ring-offset-gray-800 focus:ring-indigo-500'
                      : 'bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
                >
                  {t('settings.addSelected', 'Add Selected')}
                </button>
              </div>

              {/* Current Agents */}
              <div>
                <h4
                  className={`text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {t('settings.currentAgents', 'Current Agents')}
                </h4>
                <div
                  className={`rounded-md border overflow-y-auto max-h-96 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  {currentAgents.map((agent) => (
                    <label
                      key={agent.id}
                      className={`flex items-center p-2 ${
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCurrentAgents.includes(agent.id)}
                        onChange={(e) => {
                          setSelectedCurrentAgents(
                            e.target.checked
                              ? [...selectedCurrentAgents, agent.id]
                              : selectedCurrentAgents.filter((id) => id !== agent.id)
                          );
                        }}
                        className={`h-4 w-4 rounded ${
                          theme === 'dark'
                            ? 'text-indigo-500 border-gray-600 focus:ring-offset-gray-800'
                            : 'text-indigo-600 border-gray-300'
                        } focus:ring-indigo-500`}
                      />
                      <span
                        className={`ml-2 text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                        }`}
                      >
                        {agent.full_name || agent.email}
                      </span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={handleRemoveAgents}
                  disabled={selectedCurrentAgents.length === 0}
                  className={`mt-2 w-full inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 ${
                    theme === 'dark'
                      ? 'bg-red-600 hover:bg-red-500 disabled:bg-gray-700 disabled:text-gray-500 focus:ring-offset-gray-800 focus:ring-red-500'
                      : 'bg-red-600 hover:bg-red-700 disabled:bg-gray-300 focus:ring-offset-2 focus:ring-red-500'
                  }`}
                >
                  {t('settings.removeSelected', 'Remove Selected')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
